
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { isLineEnabled, isPusherEnabled, isChatBoardEnabled, isCruisePricingEnabled } from './config/backend';
import { isTrainingMode } from './training/trainingMode';
import CalendarV2 from './pages/CalendarV2';
import DayListReservations from './pages/DayListReservations';
import VehicleList from './pages/VehicleList';
import ReservationList from './pages/ReservationList';
import Dashboard from './pages/Dashboard';
import StockCheck from './pages/StockCheck';
import CruisePrice from './pages/CruisePrice';
import BulkMail from './pages/BulkMail';
import PaymentCheck from './pages/PaymentCheck';
import MonthlyStats from './pages/MonthlyStats';
import AiChatReview from './pages/AiChatReview';
import AiActionLog from './pages/AiActionLog';
import AiAgentLog from './pages/AiAgentLog';
import Reconcile from './pages/Reconcile';
import OtaCheck from './pages/OtaCheck';
import MailQueue from './pages/MailQueue';
import Customers from './pages/Customers';
import RequestWindow from './components/RequestWindow';
import UpdateNotifier from './components/UpdateNotifier';
import { shouldUseMobileApp } from './lib/deviceDetect';
const MobilePage = React.lazy(() => import('./pages/mobile/MobilePage'));
import { AuthProvider, useAuth, LoginForm } from './components/AuthContext';
import { setAuthToken as setDispatchAuthToken } from './api/dispatchClient';
import { ChangeManagerProvider } from './context/ChangeManagerContext';
import ChangeHistoryPanel from './components/ChangeHistoryPanel';
import ChangeHistoryIcon from './components/ChangeHistoryIcon';
import StaffSelector from './components/StaffSelector';
import NotificationPermissionPrompt from './components/NotificationPermissionPrompt';
import LineChatBoard from './components/LineChatBoard';
import StaffChatManager from './components/StaffChatManager';
import AgentFloat from './components/AgentFloat';
import { ReservationDataProvider } from './context/ReservationDataContext';
import ChatIcon from '@mui/icons-material/Chat';
import {
    AppBar, Toolbar, Typography, Button, IconButton, Menu, MenuItem, Box,
  Drawer, List, ListItem, ListItemText, ListItemIcon, Divider, useMediaQuery, useTheme, CircularProgress
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ListAltIcon from '@mui/icons-material/ListAlt';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import LogoutIcon from '@mui/icons-material/Logout';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import SessionExpiryWarning from './components/SessionExpiryWarning';
import PusherToast from './components/PusherToast';

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ChangeManagerProvider>
          <AppContent />
          <ChangeHistoryPanel />
        </ChangeManagerProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

import { useGlobalReservationSync } from './hooks/useGlobalReservationSync';
import { useReservationStatuses } from './hooks/useReservationStatuses';
import { setStatusCache } from './lib/statusMapper';

function AppContent() {
  const headerRef = useRef(null);
  const { user, loading, isAuthenticated, useMockAuth, logout, softLogin, softLoggedOut, renderKey, dispatch: authDispatch } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [calendarMenuAnchor, setCalendarMenuAnchor] = useState<HTMLElement | null>(null);
  // 貸出返却リストの hover サブメニュー（2026-08-24）。
  // ボタン⇄メニュー間をマウスが通るとき一瞬どちらからも外れるので、
  // 閉じるのを少し遅らせてチラつきを防ぐ
  const [daylistMenuAnchor, setDaylistMenuAnchor] = useState<HTMLElement | null>(null);
  const daylistMenuCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openDaylistMenu = (el: HTMLElement) => {
    if (daylistMenuCloseTimer.current) clearTimeout(daylistMenuCloseTimer.current);
    setDaylistMenuAnchor(el);
  };
  const scheduleCloseDaylistMenu = () => {
    if (daylistMenuCloseTimer.current) clearTimeout(daylistMenuCloseTimer.current);
    daylistMenuCloseTimer.current = setTimeout(() => setDaylistMenuAnchor(null), 180);
  };
  const cancelCloseDaylistMenu = () => {
    if (daylistMenuCloseTimer.current) clearTimeout(daylistMenuCloseTimer.current);
  };
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  // テスト用: ?logouttest=1 で3秒後にトークンを無効化（1回のみ）
  const logoutTestFiredRef = useRef(false);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('logouttest') === '1' && isAuthenticated && !softLoggedOut && !logoutTestFiredRef.current) {
      const timer = setTimeout(() => {
        console.log('[App] logouttest=1: invalidating token after 3s');
        logoutTestFiredRef.current = true;
        // URLからパラメータを除去
        const url = new URL(window.location.href);
        url.searchParams.delete('logouttest');
        window.history.replaceState({}, '', url.toString());
        // トークン無効化
        localStorage.removeItem('authToken');
        setDispatchAuthToken(null);
        authDispatch({ type: 'SOFT_LOGOUT' });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, softLoggedOut, authDispatch]);

  // Initialize global reservation sync (only when authenticated)
  const { pollingEnabled, pollingForced, togglePolling } = useGlobalReservationSync(user && isAuthenticated);

  // ChangeHistoryPanel にポーリング制御を公開
  useEffect(() => {
    window.__pollingControl = { pollingEnabled, pollingForced, togglePolling };
    return () => { delete window.__pollingControl; };
  }, [pollingEnabled, pollingForced, togglePolling]);

  // Pusher 接続（認証時のみ / 宮古は那覇との混線防止のため無効）
  //
  // ⚠️ softLoggedOut を必ず条件に含めること（2026-08-01 修正）。
  //    SOFT_LOGOUT はページ状態を維持するため isAuthenticated を true のまま token だけ
  //    null にする設計。そのため「セッションが終了しました」モーダル表示中でも購読が続き、
  //    未認証の画面に他ユーザーの予約変更が流れ込んでいた（情報漏れ + 再ログイン前の
  //    キャッシュ汚染）。ログアウト状態では必ず切断する。
  useEffect(() => {
    if (user && isAuthenticated && !softLoggedOut && isPusherEnabled()) {
      import('./services/pusherService').then(({ pusherService }) => {
        pusherService.connect();
      });
      return () => {
        import('./services/pusherService').then(({ pusherService }) => {
          pusherService.disconnect();
        });
      };
    }
  }, [user, isAuthenticated, softLoggedOut]);

  // ステータスマスターを取得してキャッシュに設定
  const { reservationStatuses, vehicleStatuses, vehicleLocationStatuses, vehicleTypeStatuses, reload: reloadStatuses } = useReservationStatuses();

  // ログイン後にステータスマスタを取り直す（2026-09-25）。
  // 上の取得は App のマウント時に1回だけで、ログイン前（トークン無し）だと「認証が必要です」で失敗し、
  // 以後キャッシュが空のままになっていた。PC は各ページが自前で取り直すので目立たなかったが、
  // PWA はこのキャッシュ（statusMapper）だけに頼るため、予約保存で status が解決できず
  // 免許確認・事前受付・運転者/対応者の保存が失敗していた（log_reservation_save の status_resolved:null）
  useEffect(() => {
    if (isAuthenticated && !softLoggedOut) reloadStatuses();
  }, [isAuthenticated, softLoggedOut, reloadStatuses]);

  useEffect(() => {
    if (reservationStatuses && Object.keys(reservationStatuses).length > 0) {
      setStatusCache({
        reservation: reservationStatuses,
        vehicle: vehicleStatuses,
        vehicle_location: vehicleLocationStatuses,
        vehicle_type: vehicleTypeStatuses
      });
    }
  }, [reservationStatuses, vehicleStatuses, vehicleLocationStatuses, vehicleTypeStatuses]);

  // ブラウザバック・ページ離脱防止（全ページ共通）
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    const syncHeaderHeight = () => {
      const el = headerRef.current;
      // If headerRef is not attached yet (e.g. loading), try to find by tag or wait
      // But here we only render AppBar when authenticated.
      if (!el) {
        // Fallback: if not found, maybe set a default or retry?
        // For now, let's set a reasonable default if not found, or 0.
        // Actually, if we are on a page where header is present, it should be found.
        return;
      }
      const rect = el.getBoundingClientRect();
      const style = window.getComputedStyle(el);
      const mt = parseFloat(style.marginTop || '0');
      const mb = parseFloat(style.marginBottom || '0');
      const total = Math.round(rect.height + mt + mb);
      document.documentElement.style.setProperty('--app-header-height', `${total}px`);
    };

    // Run initially and on resize
    syncHeaderHeight();
    window.addEventListener('resize', syncHeaderHeight);

    // Also run after a short delay to ensure rendering is complete
    const timer = setTimeout(syncHeaderHeight, 100);

    return () => {
      window.removeEventListener('resize', syncHeaderHeight);
      clearTimeout(timer);
    };
  }, [user, isAuthenticated, isMobile]); // Re-run when auth state or mobile state changes (header might change height)

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setDrawerOpen(false);
  };

  const location = useLocation();

  // トレーニングモード ⇄ 本番 の切替は必ずフルリロードする（2026-08-24）。
  // 同期サービス・ReservationDataContext のキャッシュはアプリ全体で共有されているため、
  // SPA 遷移のままだと切替直後に反対側のデータ（本番の予約 / ダミー）が一瞬見えてしまう。
  // ブラウザバックも含めて拾えるよう、pathname の変化で判定する。
  const wasTrainingRef = useRef(isTrainingMode());
  useEffect(() => {
    const now = isTrainingMode();
    if (now !== wasTrainingRef.current) {
      wasTrainingRef.current = now;
      window.location.reload();
    }
  }, [location.pathname]);

  // Mobile Redirect Logic
  // 「PC か PWA か」は幅ではなく端末で決める（2026-09-20。lib/deviceDetect.ts）。
  // 以前は幅 960px 未満で飛ばしていたため、PC でウィンドウを半分にするだけで
  // スマホ画面になっていた。スマホは幅に関係なく PWA、タブレットは従来どおり幅、
  // PC（タッチ無し）は幅が狭くても PC 画面。
  // isMobile（幅）は AppBar/Drawer などレイアウト調整に引き続き使う。
  const useMobileApp = shouldUseMobileApp(isMobile);
  useEffect(() => {
    if (isAuthenticated && useMobileApp && (
      location.pathname === '/' || location.pathname === '/calendar' ||
      location.pathname.startsWith('/calendar/') ||
      location.pathname === '/daylist' || location.pathname === '/vehicle_list' ||
      location.pathname === '/reservations'
    )) {
      navigate('/mobile');
    }
  }, [isAuthenticated, useMobileApp, navigate, location.pathname]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          gap: 2
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          認証確認中...
        </Typography>
      </Box>
    );
  }

  if (user && isAuthenticated) {
    const isDashboardLayout = location.pathname === '/dashboard' || location.pathname === '/stock' || location.pathname === '/ota-check' || location.pathname === '/cruise';
    const isMobileLayout = location.pathname === '/mobile';

    return (
      <ReservationDataProvider>
      <NotificationPermissionPrompt />
      {isMobileLayout ? (
        <React.Suspense fallback={
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <CircularProgress />
          </Box>
        }>
          <Routes>
            <Route path="/mobile" element={<MobilePage />} />
          </Routes>
        </React.Suspense>
      ) : isDashboardLayout ? (
        <>
        <RequestWindow />
        <Routes>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/stock" element={<StockCheck />} />
          {/* クルーズ船料金表は宮古だけ（那覇は /dashboard へ） */}
          <Route path="/cruise" element={isCruisePricingEnabled() ? <CruisePrice /> : <Navigate to="/dashboard" replace />} />
          <Route path="/ai_chat_review" element={<AiChatReview />} />
          <Route path="/ai_action_log" element={<AiActionLog />} />
          <Route path="/ai_agent_log" element={<AiAgentLog />} />
          <Route path="/reconcile" element={<Reconcile />} />
          <Route path="/mail-queue" element={<MailQueue />} />
          <Route path="/customers" element={<Customers />} />
          <Route path="/ota-check" element={<OtaCheck />} />
          <Route path="*" element={<Dashboard />} />
        </Routes>
        </>
      ) : (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }} key={`calendar-${renderKey}`}>
        <AppBar position="static" color="default" elevation={1} ref={headerRef} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
          <Toolbar>
            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Box sx={{ flexGrow: 1 }}>
              <Typography
                variant="h6"
                noWrap
                component="span"
                sx={{
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                  cursor: 'pointer',
                  '&:hover': { opacity: 0.8 },
                }}
                onClick={() => handleNavigation('/dashboard')}
              >
                {user?.company_name || 'レンタカー予約カレンダーUI'}
              </Typography>
            </Box>

            {!isMobile && (
              <Box sx={{ display: 'flex', gap: 2, mr: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <IconButton
                    color="inherit"
                    size="small"
                    onClick={(e) => setCalendarMenuAnchor(e.currentTarget)}
                    aria-label="カレンダーバリエーション"
                    sx={{ mr: -0.5 }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                  <Menu
                    anchorEl={calendarMenuAnchor}
                    open={!!calendarMenuAnchor}
                    onClose={() => setCalendarMenuAnchor(null)}
                  >
                    <MenuItem
                      onClick={() => {
                        setCalendarMenuAnchor(null);
                        handleNavigation('/calendar/us-military');
                      }}
                    >
                      カレンダー（米軍）
                    </MenuItem>
                  </Menu>
                  <Button color="inherit" onClick={() => handleNavigation('/calendar')} startIcon={<CalendarMonthIcon />}>予約カレンダー</Button>
                </Box>
                <Button color="inherit" onClick={() => handleNavigation('/reservations')} startIcon={<FormatListBulletedIcon />}>予約一覧</Button>
                <Box
                  onMouseEnter={(e) => openDaylistMenu(e.currentTarget as HTMLElement)}
                  onMouseLeave={scheduleCloseDaylistMenu}
                  sx={{ display: 'inline-flex' }}
                >
                  <Button color="inherit" onClick={() => handleNavigation('/daylist')} startIcon={<ListAltIcon />}>貸出返却リスト</Button>
                  <Menu
                    anchorEl={daylistMenuAnchor}
                    open={!!daylistMenuAnchor}
                    onClose={() => setDaylistMenuAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    MenuListProps={{
                      onMouseEnter: cancelCloseDaylistMenu,
                      onMouseLeave: scheduleCloseDaylistMenu,
                      dense: true,
                    }}
                    // hover メニューなので背景クリックを奪わない
                    sx={{ pointerEvents: 'none', '& .MuiPaper-root': { pointerEvents: 'auto' } }}
                    disableAutoFocus
                    disableEnforceFocus
                    disableRestoreFocus
                  >
                    <MenuItem onClick={() => { setDaylistMenuAnchor(null); handleNavigation('/daylist'); }}>
                      貸出返却リスト
                    </MenuItem>
                    <MenuItem onClick={() => { setDaylistMenuAnchor(null); handleNavigation('/monthly-stats'); }}>
                      月間貸出返却予定
                    </MenuItem>
                    <MenuItem
                      onClick={() => { setDaylistMenuAnchor(null); handleNavigation('/daylist/training'); }}
                      sx={{ borderTop: '1px solid #eee', color: '#b45309', fontWeight: 700 }}
                    >
                      トレーニングモード
                    </MenuItem>
                  </Menu>
                </Box>
                <Button color="inherit" onClick={() => handleNavigation('/vehicle_list')} startIcon={<DirectionsCarIcon />}>車両リスト</Button>
                {/* /line = チャット・ボード。LINE 連携か自社チャット（予約者チャット）のどちらかが有効なら出す。
                    宮古は LINE 無し・自社チャットのみなのでラベルを「チャット」にする */}
                {isChatBoardEnabled() && (
                  <Button color="inherit" onClick={() => handleNavigation('/line')} startIcon={<ChatIcon />}>{isLineEnabled() ? 'LINE' : 'チャット'}</Button>
                )}
              </Box>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* エージェントはフロート化により全ページ共通マウントへ移動（§16-4 S1） */}
              <StaffSelector user={user} />
              <ChangeHistoryIcon />
              <IconButton color="error" onClick={() => {
                logout();
              }} size="small">
                <LogoutIcon />
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>
        <SessionExpiryWarning />
        <PusherToast />

        <Drawer
          anchor="left"
          open={drawerOpen}
          onClose={handleDrawerToggle}
        >
          <Box sx={{ width: 250 }} role="presentation">
            <List>
              {/* モバイル端末のみ: PWA（/mobile）への戻り動線。
                  /line 等は desktop レイアウトで開くため、PWA から遷移した後に戻る手段が必要 */}
              {isMobile && (
                <>
                  <ListItem {...{button: true} as any} onClick={() => handleNavigation('/mobile')}>
                    <ListItemIcon><PhoneIphoneIcon sx={{ color: '#0ea5e9' }} /></ListItemIcon>
                    <ListItemText primary="モバイル画面に戻る" sx={{ '& .MuiListItemText-primary': { color: '#0ea5e9', fontWeight: 700 } }} />
                  </ListItem>
                  <Divider />
                </>
              )}
              <ListItem {...{button: true} as any} onClick={() => handleNavigation('/calendar')}>
                <ListItemIcon><CalendarMonthIcon /></ListItemIcon>
                <ListItemText primary="予約カレンダー" />
              </ListItem>
              <ListItem {...{button: true} as any} onClick={() => handleNavigation('/reservations')}>
                <ListItemIcon><FormatListBulletedIcon /></ListItemIcon>
                <ListItemText primary="予約一覧" />
              </ListItem>
              <ListItem {...{button: true} as any} onClick={() => handleNavigation('/daylist')}>
                <ListItemIcon><ListAltIcon /></ListItemIcon>
                <ListItemText primary="貸出返却リスト" />
              </ListItem>
              <ListItem {...{button: true} as any} onClick={() => handleNavigation('/vehicle_list')}>
                <ListItemIcon><DirectionsCarIcon /></ListItemIcon>
                <ListItemText primary="車両リスト" />
              </ListItem>
              {isChatBoardEnabled() && (
                <ListItem {...{button: true} as any} onClick={() => handleNavigation('/line')}>
                  <ListItemIcon><ChatIcon /></ListItemIcon>
                  <ListItemText primary={isLineEnabled() ? 'LINE' : 'チャット'} />
                </ListItem>
              )}
            </List>
            <Divider />
            <List>
              <ListItem>
                <ListItemText
                  primary={user?.username}
                  secondary={useMockAuth ? '📱 モック認証' : '🔗 PHP認証'}
                />
              </ListItem>
            </List>
          </Box>
        </Drawer>

        {/* スタッフチャット(右端の STAFF ボタン)は LINE 連携そのもの。
            LINE 無効店舗(宮古)では出さない。2026-07-30 まで無条件描画だったため
            宮古の全ページに那覇のスタッフ LINE 一覧が出ていた */}
        {isLineEnabled() && <StaffChatManager />}
        <RequestWindow />
        <UpdateNotifier />
        {/* 直近概要（RecentSummaryFloat）は 2026-07-26 廃止（ダイジェスト/実況ウィンドウに置換） */}
        <Box component="main" sx={{ flexGrow: 1, overflow: 'auto' }}>
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/calendar" element={<CalendarV2 />} />
            <Route path="/calendar/us-military" element={<CalendarV2 vehicleStatusFilterId={6} />} />
            <Route path="/reservations" element={<ReservationList />} />
            <Route path="/daylist" element={<DayListReservations />} />
            {/* トレーニングモード（2026-08-24）: ダミーデータで daylist を練習する。
                判定は training/trainingMode.ts が pathname で行うため、パスを変える場合は
                TRAINING_PATH も合わせて変更すること */}
            <Route path="/daylist/training" element={<DayListReservations />} />
            {/* /daylist_old（旧版）は 2026-08-04 廃止。ブックマーク互換のため新版へリダイレクト */}
            <Route path="/daylist_old" element={<Navigate to="/daylist" replace />} />
            <Route path="/vehicle_list" element={<VehicleList />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/line" element={isChatBoardEnabled() ? <LineChatBoard /> : <Navigate to="/dashboard" replace />} />
            <Route path="/stock" element={<StockCheck />} />
            <Route path="/main_send" element={<BulkMail />} />
            <Route path="/payment_check" element={<PaymentCheck />} />
            <Route path="/monthly-stats" element={<MonthlyStats />} />
            {/* 旧パス /staff（2026-08-04 に /monthly-stats へ改称）の互換リダイレクト */}
            <Route path="/staff" element={<Navigate to="/monthly-stats" replace />} />
            <Route path="/ai_chat_review" element={<AiChatReview />} />
            <Route path="/ai_action_log" element={<AiActionLog />} />
            <Route path="/ai_agent_log" element={<AiAgentLog />} />
            <Route path="/reconcile" element={<Reconcile />} />
            <Route path="/mail-queue" element={<MailQueue />} />
            <Route path="/customers" element={<Customers />} />
            <Route path="/ota-check" element={<OtaCheck />} />
          </Routes>
        </Box>
      </Box>
      )}
      {/* エージェント（フロート・全デスクトップページ共通。●に最小化してもページを跨いで残る） */}
      {!isMobile && !location.pathname.startsWith('/mobile') && <AgentFloat />}

      {/* ソフトログアウト: 現在のページに滞在したままログインモーダル */}
      {softLoggedOut && createPortal(
        <Box sx={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          zIndex: 99999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.6)',
          backdropFilter: 'blur(4px)',
          pointerEvents: 'none',
        }}>
          <Box sx={{
            background: '#fff', borderRadius: 3, p: 0,
            width: '100%', maxWidth: 420,
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            overflow: 'hidden',
            pointerEvents: 'auto',
          }}>
            <Box sx={{ p: 3, pb: 1, textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                セッションが終了しました
              </Typography>
              <Typography variant="body2" color="text.secondary">
                再度ログインしてください。現在のページは維持されます。
              </Typography>
            </Box>
            <LoginForm
              key={`soft-login-${renderKey}`}
              embedded
              onSuccess={() => {
                // LOGIN_SUCCESS で softLoggedOut: false になる
                // データを再読み込み
                import('./services/reservationSyncService').then(({ reservationSyncService }) => {
                  reservationSyncService.forceSync();
                });
                import('./services/vehicleSyncService').then(({ vehicleSyncService }) => {
                  vehicleSyncService.forceSync();
                });
              }}
            />
          </Box>
        </Box>,
        document.body
      )}
      </ReservationDataProvider>
    );
  }

  return <LoginForm key={`login-${renderKey}`} />;
}

export default App;
